import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#3AAF58', blue:'#FFFFFF',
 red:'#E05050', redBg:'rgba(224,80,80,.10)', redBorder:'rgba(224,80,80,.25)',
 amber:'#D4840F', amberBg:'rgba(212,132,15,.10)', amberBorder:'rgba(212,132,15,.25)',
 green:'#3AAF58', greenBg:'rgba(58,175,88,.10)',
 purple:'#8060C0', teal:'#0D9488',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const MEAL_LABELS = {
 breakfast: { label:' Breakfast', color:C.amber },
 lunch: { label:' Lunch', color:C.accent },
 hi_tea: { label:' Hi-Tea', color:C.teal },
 dinner: { label:' Dinner', color:C.blue },
 gala_dinner: { label:' Gala Dinner', color:C.purple },
 welcome_drink:{ label:' Welcome Drink',color:C.green },
 total_covers: { label:' Total Covers', color:C.text },
};

export default function MealForecast() {
 const [date, setDate] = useState(new Date().toISOString().slice(0,10));
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');

 const load = useCallback(async () => {
 setLoading(true); setError('');
 try {
 const r = await fetch(`${API}/kitchen/meal-forecast?date=${date}`);
 const d = await r.json();
 if (!r.ok) throw new Error(d.error||'Failed');
 setData(d);
 } catch(e) { setError(e.message); }
 finally { setLoading(false); }
 }, [date]);

 useEffect(() => { load(); }, [load]);

 const inp = { padding:'9px 12px', background:C.surface2, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:14, outline:'none' };

 return (
 <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'Inter,sans-serif' }}>

 {/* Header */}
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
 <div>
 <div style={{ fontSize:22, fontWeight:800 }}> Meal Forecast</div>
 <div style={{ fontSize:13, color:C.text2, marginTop:2 }}>Live meal counts from confirmed bookings</div>
 </div>
 <div style={{ display:'flex', gap:10, alignItems:'center' }}>
 <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={inp} />
 <button onClick={()=>setDate(new Date().toISOString().slice(0,10))}
 style={{ padding:'9px 16px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.accent, cursor:'pointer', fontSize:13, fontWeight:600 }}>Today</button>
 <button onClick={load}
 style={{ padding:'9px 16px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}></button>
 </div>
 </div>

 {error && <div style={{ padding:'10px 14px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, color:C.red, fontSize:13, marginBottom:16 }}> {error}</div>}
 {loading && <div style={{ textAlign:'center', padding:60, color:C.text2 }}>Loading meal forecast…</div>}

 {!loading && data && (
 <>
 {/* Meal Summary Cards */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12, marginBottom:24 }}>
 {Object.entries(MEAL_LABELS).map(([key, cfg]) => (
 <div key={key} style={{ background:C.surface, border:`1px solid ${cfg.color}33`, borderRadius:14, padding:'16px 18px' }}>
 <div style={{ fontSize:11, color:C.text2, fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>{cfg.label}</div>
 <div style={{ fontSize:36, fontWeight:900, color:cfg.color }}>{data.mealSummary?.[key] || 0}</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:3 }}>pax</div>
 </div>
 ))}
 </div>

 {/* Booking wise breakdown */}
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden', marginBottom:20 }}>
 <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text }}> Booking-wise Breakdown ({data.bookings?.length || 0} bookings)</div>
 <div style={{ fontSize:13, color:C.text2 }}>{fmtDate(date)}</div>
 </div>

 {data.bookings?.length === 0 ? (
 <div style={{ textAlign:'center', padding:60, color:C.text2 }}>
 <div style={{ fontSize:40, marginBottom:12 }}></div>
 <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>No bookings on this date</div>
 <div style={{ fontSize:13 }}>Select a date with confirmed bookings</div>
 </div>
 ) : (
 <div style={{ overflowX:'auto' }}>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <thead>
 <tr style={{ borderBottom:`1px solid ${C.border}` }}>
 {['Code','Guest/Group','Type','Pax','Breakfast','Lunch','Hi-Tea','Dinner','Gala','Notes'].map(h=>(
 <th key={h} style={{ padding:'10px 14px', fontSize:11, color:C.text2, fontWeight:700, textAlign:'left', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {data.bookings?.map((b,i) => {
 const fd = b.foodData;
 // Calculate meal pax for this booking
 let bfPax=0, lunchPax=0, hiTeaPax=0, dinnerPax=0, galaPax=0;
 if (fd) {
 if (fd.type==='wedding') {
 (fd.days||[]).forEach(day => {
 Object.entries(day.meals||{}).forEach(([mt,md]) => {
 const p = md.pax||b.pax_count||0;
 if (mt==='breakfast') bfPax += p;
 if (mt==='lunch') lunchPax += p;
 if (mt==='hi_tea') hiTeaPax += p;
 if (mt==='dinner') dinnerPax += p;
 if (mt==='gala_dinner') galaPax += p;
 });
 });
 }
 if (fd.type==='corporate') {
 Object.entries(fd.meals||{}).forEach(([k,md]) => {
 const p = md.pax||fd.pax||b.pax_count||0;
 if (k.includes('breakfast')) bfPax += p;
 if (k.includes('lunch')||k.includes('buffet')) lunchPax += p;
 if (k.includes('hi_tea')) hiTeaPax += p;
 if (k.includes('dinner')) dinnerPax+= p;
 });
 }
 if (fd.type==='group') { (fd.meals||[]).forEach(meal=>{ const mt=(meal.meal_type||'').toLowerCase(); const p=meal.pax||b.pax_count||0; if(mt.includes('breakfast')) bfPax+=p; if(mt.includes('lunch')) lunchPax+=p; if(mt.includes('hitea')||mt.includes('snack')) hiTeaPax+=p; if(mt.includes('dinner')) dinnerPax+=p; }); } if (fd.type==='room_stay') {
 const p = fd.mealPax||b.pax_count||0;
 (fd.meals||[]).forEach(m => {
 if (m==='cp'||m==='map'||m==='ap'||m.includes('breakfast')) bfPax += p;
 if (m.includes('lunch')||m.includes('buffet')) lunchPax += p;
 if (m==='map'||m==='ap'||m.includes('dinner')) dinnerPax += p;
 });
 }
 }
 const hasFood = bfPax||lunchPax||hiTeaPax||dinnerPax||galaPax;
 return (
 <tr key={b.id} style={{ borderBottom:`1px solid rgba(255,255,255,.04)`, background:i%2===0?'transparent':`${C.surface2}60` }}>
 <td style={{ padding:'10px 14px', fontSize:12, fontWeight:700, color:C.accent, whiteSpace:'nowrap' }}>{b.booking_code}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text }}>{b.guest_name||'—'}</td>
 <td style={{ padding:'10px 14px', fontSize:11, color:C.text2, whiteSpace:'nowrap' }}>{b.booking_type}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:700, color:C.text, textAlign:'center' }}>{b.pax_count}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:bfPax>0?700:400, color:bfPax>0?C.amber:C.text3, textAlign:'center' }}>{bfPax||'—'}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:lunchPax>0?700:400, color:lunchPax>0?C.accent:C.text3, textAlign:'center' }}>{lunchPax||'—'}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:hiTeaPax>0?700:400, color:hiTeaPax>0?C.teal:C.text3, textAlign:'center' }}>{hiTeaPax||'—'}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:dinnerPax>0?700:400, color:dinnerPax>0?C.blue:C.text3, textAlign:'center' }}>{dinnerPax||'—'}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:galaPax>0?700:400, color:galaPax>0?C.purple:C.text3, textAlign:'center' }}>{galaPax||'—'}</td>
 <td style={{ padding:'10px 14px', fontSize:11, color:hasFood?C.accent:C.text3 }}>{hasFood?' Food selected':'No menu yet'}</td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}
 </div>

 {/* Print Button */}
 <div style={{ display:'flex', gap:10 }}>
 <button onClick={()=>window.print()}
 style={{ padding:'10px 20px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>
 Print Meal Forecast
 </button>
 <button onClick={load}
 style={{ padding:'10px 20px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}>
 Refresh
 </button>
 </div>
 </>
 )}
 </div>
 );
}
